import { LocationIcon } from '@/assets/icons'
import PageLoader from '@components/PageLoader'
import { useBreadcrumbs } from '@context/BreadcrumbContext'
import { useEvents, useTrip } from '@hooks'
import { TripHelper } from '@lib/TripHelper'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import DaysFilter from './components/DaysFilter'
import EventList from './components/EventList'
import MapCanvas from './components/MapCanvas'
import {
	ContentRow,
	EmptyState,
	EmptyStateButton,
	EmptyStateContainer,
	EmptyStateIcon,
	EmptyStateText,
	EmptyStateTitle,
	ErrorContainer,
	LoadingContainer,
	MapWrapper,
	PageContainer,
	PageHeader,
	PageTitle,
	SidePanel,
} from './TripMapPage.styles'
import { numberMappedEvents, splitEventsByGeometry } from './utils'

const TripMapPage: React.FC = () => {
	const { tripId } = useParams<{ tripId: string }>()
	const navigate = useNavigate()
	const { data: trip, isLoading: tripLoading, error: tripError } = useTrip(tripId)
	const { data: eventsData, isLoading: eventsLoading, error: eventsError } = useEvents(tripId)
	const [selectedDays, setSelectedDays] = useState<Set<number>>(new Set())
	const [selectedEventId, setSelectedEventId] = useState<number | null>(null)
	const [hasInitialized, setHasInitialized] = useState(false)

	useBreadcrumbs(
		trip
			? [
					{ label: 'My Trips', path: '/trips' },
					{ label: trip.name, path: `/trips/${tripId}` },
					{ label: 'Itinerary', path: `/trips/${tripId}/itinerary` },
					{ label: 'Map' },
				]
			: []
	)

	const dayCount = useMemo(() => {
		if (!trip) return 0
		const helper = new TripHelper(trip)
		return helper.hasValidDates() ? trip.dayCount ?? 0 : 0
	}, [trip])

	const split = useMemo(() => splitEventsByGeometry(eventsData?.events), [eventsData])

	const mappedCountByDay = useMemo(() => {
		const counts = new Map<number, number>()
		split.mapped.forEach(m => counts.set(m.dayIndex, (counts.get(m.dayIndex) ?? 0) + 1))
		return counts
	}, [split.mapped])

	useEffect(() => {
		if (hasInitialized || split.mapped.length === 0) return
		const firstDayWithPins = split.mapped[0].dayIndex
		setSelectedDays(new Set([firstDayWithPins]))
		setHasInitialized(true)
	}, [split.mapped, hasInitialized])

	const filteredMapped = useMemo(
		() => numberMappedEvents(split.mapped.filter(m => selectedDays.has(m.dayIndex))),
		[split.mapped, selectedDays]
	)

	const filteredUnmapped = useMemo(
		() => split.unmapped.filter(u => selectedDays.has(u.dayIndex)),
		[split.unmapped, selectedDays]
	)

	const handleDaysChange = useCallback((next: Set<number>) => {
		setSelectedDays(next)
		setSelectedEventId(null)
	}, [])

	const handleSelect = useCallback((eventId: number) => {
		setSelectedEventId(prev => (prev === eventId ? null : eventId))
	}, [])

	const goBack = useCallback(() => {
		navigate(`/trips/${tripId}/itinerary`)
	}, [navigate, tripId])

	if (tripLoading || eventsLoading) {
		return (
			<LoadingContainer>
				<PageLoader label="Loading map…" />
			</LoadingContainer>
		)
	}

	if (tripError || eventsError) {
		return <ErrorContainer>Error loading map: {tripError?.message || eventsError?.message}</ErrorContainer>
	}

	if (!trip) {
		return <LoadingContainer>Trip not found</LoadingContainer>
	}

	const totalMapped = split.mapped.length

	if (totalMapped === 0) {
		return (
			<PageContainer>
				<PageHeader>
					<PageTitle>Trip Map</PageTitle>
				</PageHeader>
				<EmptyStateContainer>
					<EmptyState>
						<EmptyStateIcon>
							<LocationIcon width={96} height={96} fill="currentColor" />
						</EmptyStateIcon>
						<EmptyStateTitle>No locations on the map yet</EmptyStateTitle>
						<EmptyStateText>
							Add a location to an event in your itinerary and it will show up here.
						</EmptyStateText>
						<EmptyStateButton onClick={goBack}>Back to itinerary</EmptyStateButton>
					</EmptyState>
				</EmptyStateContainer>
			</PageContainer>
		)
	}

	return (
		<PageContainer>
			<PageHeader>
				<PageTitle>Trip Map</PageTitle>
			</PageHeader>
			<ContentRow>
				<SidePanel>
					<DaysFilter
						dayCount={dayCount}
						selectedDays={selectedDays}
						mappedCountByDay={mappedCountByDay}
						onChange={handleDaysChange}
					/>
					<EventList
						mapped={filteredMapped}
						unmapped={filteredUnmapped}
						selectedEventId={selectedEventId}
						onSelect={handleSelect}
					/>
				</SidePanel>
				<MapWrapper>
					<MapCanvas
						events={filteredMapped}
						selectedEventId={selectedEventId}
						onMarkerClick={handleSelect}
					/>
				</MapWrapper>
			</ContentRow>
		</PageContainer>
	)
}

export default TripMapPage
